import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Upload, X, Loader2, Image as ImageIcon, Video } from "lucide-react";

export default function FileUploader({ value = [], onChange, accept = "image/*,video/*" }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const isVideo = (url) => /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
  const isImage = (url) => /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url) || (!isVideo(url) && url);

  const upload = async (files) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    setUploading(true);
    const newUrls = [];
    for (const f of arr) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(`${API}/admin/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Resolve absolute URL via REACT_APP_BACKEND_URL
        const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
        newUrls.push(url);
        toast.success(`Uploaded ${f.name}`);
      } catch (err) {
        toast.error(`Failed: ${f.name} — ${err.response?.data?.detail || "upload error"}`);
      }
    }
    onChange([...(value || []), ...newUrls]);
    setUploading(false);
  };

  const onPick = (e) => upload(e.target.files);
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    upload(e.dataTransfer.files);
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div data-testid="file-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
          dragOver ? "border-[#8B1E28] bg-[#FFF5F3]" : "border-[#EAE3D9] bg-[#FDFBF7] hover:bg-[#FFF5F3]/60"
        }`}
        data-testid="dropzone"
      >
        <input
          ref={inputRef} type="file" multiple accept={accept}
          onChange={onPick} className="hidden" data-testid="file-input"
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-[#595552]">
            <Loader2 className="h-6 w-6 animate-spin text-[#8B1E28]" />
            <span className="text-sm">Uploading…</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-[#595552]">
            <Upload className="h-7 w-7 text-[#C5A059]" />
            <p className="text-sm font-medium text-[#2C2A29]">Drop images / videos here</p>
            <p className="text-xs text-[#8A8480]">or click to browse · JPG / PNG / WEBP / MP4 up to 25 MB each</p>
          </div>
        )}
      </div>

      {value && value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4" data-testid="uploaded-grid">
          {value.map((url, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-[#EAE3D9] bg-[#FFF5F3] aspect-square">
              {isVideo(url) ? (
                <video src={url} className="w-full h-full object-cover" muted />
              ) : isImage(url) ? (
                <img src={url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full"><ImageIcon className="h-6 w-6 text-[#8A8480]" /></div>
              )}
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(i); }}
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-asset-${i}`}>
                <X className="h-3 w-3 text-[#8B1E28]" />
              </button>
              {isVideo(url) && (
                <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Video className="h-3 w-3" /> Video
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
